/* eslint-disable class-methods-use-this */
import {
  type Stripe,
  type StripeElements,
  type StripeError,
  type ConfirmAffirmPaymentData,
  loadStripe,
  CreateTokenCardData,
  StripeElementsOptions,
  StripeElementsOptionsMode,
} from "@stripe/stripe-js";
import UserBasedTicketBasketRepository from "@app-contexts/user/features/basket/data/repositories/UserBasedTicketBasketRepository/UserBasedTicketBasketRepository";
import { SITE_URL } from "@shared/env";
import { DataProvider } from "@shared/constants/common";
import VenueBasedTicketBasketRepository from "@app-contexts/dashboard/features/box-office/basket/data/repositories/VenueBasedTicketBasketRepository";
import BasketRepositoryFactory from "@shared/modules/basket/factory/BasketRepositoryFactory";
import { convertPriceStringToInteger } from "@shared/modules/financials/utils/price-utils";
import {
  PURCHASE_SOURCE_PLATFORM_MAP,
  STRIPE_PAYMENT_INTENT_STATUS_MAP,
} from "@shared/constants/enums";
import { formatCountryToISOCode } from "@shared/utils/addresses/address-utils";
import PublicBasedVenuesRepository from "@shared/modules/venue/repositories/PublicBasedVenuesRepository";
import { PAYMENT_MANAGER_CONFIRMATION_STRATEGY } from "@shared/modules/payments/utils/manager/utils";
import { ensuredUrl, securedUrl } from "@shared/utils/urls/url-utils";
import {
  extractErrorMessage,
  getExceptionToastDescription,
} from "@shared/utils/errors/error-utils";

import { BasketRequestQueue } from "@shared/modules/basket/services/BasketRequestQueue/BasketRequestQueue";

import {
  buildStripeAppearance,
  resolveStripeColorMode,
  type StripeColorMode,
} from "../../utils/stripe/stripe-theme-utils";
import PaymentGateway, {
  type UpdatePaymentObjectOptions,
} from "../PaymentGateway";
import { PaymentServiceBasket } from "../../services/useBasketPaymentService";
import { PurchaseOptsType } from "../../types";

import { PaymentConfigurationError } from "../../utils/errors/PaymentConfigurationError";
import { PaymentInitializationError } from "../../utils/errors/PaymentInitializationError";
import { PaymentProcessingError } from "../../utils/errors/PaymentProcessingError";
import { StripeWalletPaymentError } from "../../utils/stripe/errors/StripeWalletPaymentError";
import { PaymentConfirmationError } from "../../utils/errors/PaymentConfirmationError";
import { isPaymentOrchestrationError } from "../../utils/errorUtils";
import { serializeStripeError } from "../../utils/stripe/stripe";
import { createPaymentLogger } from "../../utils/payment-logger";
import {
  buildBasePurchaseData,
  resolveAddresses,
} from "../../utils/purchase/build-purchase-data";

const log = createPaymentLogger("StripePaymentGateway");
const basketRequestQueue = BasketRequestQueue.getInstance();

/**
 * Build the URL Stripe/Affirm should redirect back to after off-site payment.
 *
 * Priority:
 * 1. `redirect-url` query param (cross-frame, e.g. SDK → widget iframe)
 * 2. Current page URL
 */
const appendStripeParam = (rawUrl: string): string => {
  const parsed = new URL(rawUrl);
  parsed.searchParams.set("stripe", "true");
  return parsed.toString();
};

const getRedirectReturnUrl = (): string => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectUrl = urlParams.get("redirect-url");

    if (redirectUrl) {
      return appendStripeParam(decodeURIComponent(redirectUrl));
    }

    return appendStripeParam(window.location.href);
  } catch {
    return appendStripeParam(`${SITE_URL}checkout/purchase/`);
  }
};

class StripePaymentGateway extends PaymentGateway {
  public static gatewayName = "StripePaymentGateway";

  instance: Stripe | null = null;
  elementsInstance: StripeElements | undefined = undefined;
  initializationPromise: Promise<Stripe | null> | undefined = undefined;
  clientSecret: string | undefined = undefined;
  mode: StripeElementsOptionsMode["mode"] = "payment";

  venuePaymentIntentsEnabled: boolean | undefined = undefined;

  /**
   * Initializes Stripe by calling loadStripe from the stripejs loader library.
   *
   * It should be noted that this won't necessarily load the script onto the page if it has
   * been loaded previously.
   * @see {@link https://github.com/stripe/stripe-js/blob/master/src/shared.ts} to see how this function is implemented
   */
  async initialize(
    basket: PaymentServiceBasket,
    opts: {
      mode?: StripeElementsOptionsMode["mode"];
    } = {
      mode: "payment",
    }
  ) {
    log.info("Initializing");

    // if there is already a promise pending, just await that
    if (!this.initializationPromise) {
      if (!basket.paymentGateway) {
        throw new PaymentConfigurationError(
          "Unavailable paymentGateway on basket to use for initialization"
        );
      }

      this.initializationPromise = loadStripe(
        basket.paymentGateway.publishable_api_key,
        {
          ...(basket.paymentGateway.access_granted_account_id && {
            stripeAccount: basket.paymentGateway.access_granted_account_id,
          }),
        }
      );
    }

    try {
      // assert this as Stripe, as we shouldn't be in a server environment
      // @see https://github.com/stripe/stripe-js/issues/9
      this.instance = (await this.initializationPromise) as Stripe;
    } catch (error) {
      // Clear the failed promise to allow retries on subsequent calls
      this.initializationPromise = undefined;
      this.instance = null;
      this.elementsInstance = undefined;

      log.error("Initialization failed, cleared for retry", {
        errorMessage: extractErrorMessage(error),
      });

      if (isPaymentOrchestrationError(error)) {
        throw error;
      }

      throw new PaymentInitializationError(
        `Stripe SDK initialization failed: ${extractErrorMessage(error)}`,
        {
          metadata: {
            gatewayName: StripePaymentGateway.gatewayName,
            operation: "initialize",
            originalError: extractErrorMessage(error),
          },
        }
      );
    }

    this.mode = opts.mode;

    const stripeElementsOptions: StripeElementsOptions = {
      // /**
      //  * Use mode here instead of clientSecret.
      //  *
      //  * We use both interchangeably, for example Card elements do not require a clientSecret for element creation,
      //  * and neither do SetupIntents:
      //  * https://docs.stripe.com/js/elements_object/create_without_intent
      //  * but are necessary to confirm a card payment:
      //  * https://docs.stripe.com/js/payment_intents/confirm_card_payment
      //  *
      //  * Wallets and affirm also both don't need a clientSecret on element creation, but is required for confirmation:
      //  * https://docs.stripe.com/js/elements_object/express_checkout_element_confirm_event
      //  * https://docs.stripe.com/js/payment_intents/confirm_payment
      //  * and
      //  * https://docs.stripe.com/js/payment_intents/confirm_affirm_payment
      //  */
      // mode,
      // // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // // @ts-ignore -- false positive type error
      // currency: basket.currency.toLowerCase(),
      // // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // // @ts-ignore -- false positive type error
      // amount: convertPriceStringToInteger(basket.totalPrice),
      // // The setup_future_usage parameter MUST match the setup_future_usage parameter that the backend uses to create the PI object.
      // // At the time of writing, this is not dynamic and is statically always `on_session`.
      // setup_future_usage: "on_session",
      // Load custom fonts for Stripe Elements iframe
      // https://docs.stripe.com/js/elements_object/create#stripe_elements-options-fonts
      fonts: [
        {
          cssSrc:
            "https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap",
        },
      ],
      // For elements that support the Elements Appearance API.
      // Uses the current color mode; live-updated later via updateAppearance().
      appearance: buildStripeAppearance(resolveStripeColorMode()),
    };

    if (opts.mode === "payment") {
      /**
       * Use mode here instead of clientSecret.
       *
       * We use both interchangeably, for example Card elements do not require a clientSecret for element creation
       * https://docs.stripe.com/js/elements_object/create_without_intent
       * but are necessary to confirm a card payment:
       * https://docs.stripe.com/js/payment_intents/confirm_card_payment
       *
       * Wallets and affirm also both don't need a clientSecret on element creation, but is required for confirmation:
       * https://docs.stripe.com/js/elements_object/express_checkout_element_confirm_event
       * https://docs.stripe.com/js/payment_intents/confirm_payment
       * and
       * https://docs.stripe.com/js/payment_intents/confirm_affirm_payment
       */
      stripeElementsOptions.mode = "payment";
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore -- false positive type error
      stripeElementsOptions.currency = basket.currency.toLowerCase();
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore -- false positive type error
      stripeElementsOptions.amount = convertPriceStringToInteger(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore -- false positive type error
        basket.totalPrice
      );
      // The setup_future_usage parameter MUST match the setup_future_usage parameter that the backend uses to create the PI object.
      // At the time of writing, this is not dynamic and is statically always `on_session`.
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore -- false positive type error
      stripeElementsOptions.setup_future_usage = "on_session";
    } else {
      /**
       * Use mode here instead of clientSecret.
       *
       * SetupIntents do not require a client secret
       * https://docs.stripe.com/js/elements_object/create_without_intent
       */
      stripeElementsOptions.mode = "setup";
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore -- false positive type error
      stripeElementsOptions.currency = basket.currency.toLowerCase();
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore -- false positive type error
      stripeElementsOptions.setup_future_usage = "off_session";
    }

    // Fetch venue configuration for `use_stripe_payment_intents` value
    const venue = await new PublicBasedVenuesRepository().retrieve(
      basket.paymentVenue
    );

    this.venuePaymentIntentsEnabled = venue.use_stripe_payment_intents;

    if (this.config.walletsEnabled || this.venuePaymentIntentsEnabled) {
      // Create payment objects if wallets or venue has PI enabled
      await this.updatePaymentObject(basket, {
        forceUpsert: true,
      });
    }

    if (!this.elementsInstance) {
      // create elements instance
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore -- false positive type error
      this.elementsInstance = this.instance.elements(stripeElementsOptions);
    }

    return this;
  }

  /**
   * Live-updates the appearance on the shared elementsInstance so that all
   * Appearance-API elements (wallet, PaymentElement, etc.) reflect the new
   * color mode without being destroyed and recreated.
   */
  updateAppearance(mode: StripeColorMode): void {
    if (!this.elementsInstance) return;
    this.elementsInstance.update({
      appearance: buildStripeAppearance(mode),
    });
  }

  public async savePaymentMethod(
    _: DataProvider,
    basket: PaymentServiceBasket
  ) {
    // Save the payment method by creating a SetupIntent object.
    // Note that confirmation of a SetupIntent does not happen here, as at the time of writing
    // confirmation of a SetupIntent is directly tied to purchasing a waitlist basket in the backend.
    // @see confirmDeferredPayment for more details.
    return this.upsertSetupIntent(basket);
  }

  public async updatePaymentObject(
    basket: Pick<
      PaymentServiceBasket,
      | "basketId"
      | "currency"
      | "totalPrice"
      | "paymentIntentStatus"
      | "isWaitlist"
      | "userId"
    >,
    options: UpdatePaymentObjectOptions = {}
  ) {
    const { forceUpsert } = options;
    if (
      basket.paymentIntentStatus ===
      STRIPE_PAYMENT_INTENT_STATUS_MAP.SUCCEEDED.id
    ) {
      log.info(
        `Ignoring updatePaymentObject — basket.payment_intent_status is ${basket.paymentIntentStatus}`
      );

      return this;
    }

    log.info(`Updating payment object in ${this.mode} mode`);

    if (this.mode === "payment") {
      this.elementsInstance?.update({
        mode: "payment",
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore -- false positive type error
        currency: basket.currency.toLowerCase(),
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore -- false positive type error
        amount: convertPriceStringToInteger(basket.totalPrice),
      });

      log.info("Updated payment elements");

      /* Check if clientSecret exists.
       * This prevents pre-mature PI/SI upserts when we don't need to, by relying on `initialize` to
       * have created the PI/SI beforehand and thus this.clientSecret is truthy.
       *
       * Force upsert allows confirmation flows to forcefully create the PI/SI even if one does not exist. Imagine:
       * - config.walletsEnabled=False
       * - venue.use_stripe_payment_intents=False
       * - venue.affirm_is_enabled=True
       * and the user confirms with Affirm.
       *
       * This aids in path isolation. Otherwise, Affirm will have to rely on the other pre-existing conditions (like payment intents) just to proceed.
       * */
      if (this.clientSecret || forceUpsert) {
        await this.upsertPaymentIntent(basket);
        log.info(`Upserted PaymentIntent via ${this.mode} mode`);
      } else {
        log.info(
          `Skipping updatePaymentObject — no clientSecret or forceUpsert in ${this.mode} mode`
        );
      }
    } else if (this.mode === "setup") {
      this.elementsInstance?.update({
        mode: "setup",
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore -- false positive type error
        currency: basket.currency.toLowerCase(),
        // `amount` is not permitted when `mode` is `"setup"`.
      });

      log.info("Updated payment elements");

      // SetupIntents require a User attached to the basket
      if (basket.userId != null) {
        await this.upsertSetupIntent(basket);
        log.info(`Upserted SetupIntent via ${this.mode} mode`);
      } else {
        log.info(
          `Skipping updatePaymentObject — basket.userId is ${basket.userId} in ${this.mode} mode`
        );
      }
    }

    log.info("Updated payment object");

    return this;
  }

  /**
   * Creates a Payment Intent based on the basket.
   * If the stripe_wallet_payment_intent_id already has a value, that value is returned
   * to avoid creating a new PaymentIntent
   */
  private async upsertPaymentIntent(
    basket: Pick<
      PaymentServiceBasket,
      "basketId" | "basketUuid" | "paymentIntentStatus"
    >
  ) {
    if (basket?.basketId == null) {
      throw new PaymentProcessingError(
        "Tried to create a payment intent for an invalid basket"
      );
    }

    if (basket.paymentIntentStatus === "succeeded") {
      return this.clientSecret;
    }

    const paymentIntent = await basketRequestQueue.addBasketRequest(
      async () => {
        // UB endpoint is used on both public and box office
        return new UserBasedTicketBasketRepository().upsertPaymentIntent({
          id: basket.basketId,
          uuid: basket.basketUuid || "",
        });
      }
    );

    this.clientSecret = paymentIntent.client_secret;

    log.info("Created or updated payment intent");

    return this.clientSecret;
  }

  private async upsertSetupIntent(
    basket: Pick<
      PaymentServiceBasket,
      "basketId" | "basketUuid" | "purchaseSourcePlatform"
    >
  ) {
    if (basket?.basketId == null) {
      throw new PaymentProcessingError(
        "Tried to create a setup intent for an invalid basket"
      );
    }

    // Guard against accidentally saving a payment method for a venue user
    const BOX_OFFICE_PURCHASE_PLATFORMS = {
      [PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB_BOX_OFFICE.id]: true,
      [PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_DESKTOP_BOX_OFFICE.id]: true,
      [PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_MOBILE_BOX_OFFICE.id]: true,
      [PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_QPOS.id]: true,
    };

    if (
      typeof basket.purchaseSourcePlatform === "string" &&
      BOX_OFFICE_PURCHASE_PLATFORMS[
        basket.purchaseSourcePlatform as keyof typeof BOX_OFFICE_PURCHASE_PLATFORMS
      ]
    ) {
      throw new PaymentConfigurationError(
        "Tried to save a payment method for a box office user instead of a customer",
        {
          userMessage:
            "We currently do not support saving payment methods on behalf of a customer.",
        }
      );
    }

    // Saving payment methods via Stripe SetupIntents is only supported for logged-in users, hence the UB repository.
    const setupIntent = await basketRequestQueue.addBasketRequest(async () => {
      return new UserBasedTicketBasketRepository().upsertSetupIntent({
        id: basket.basketId,
        uuid: basket.basketUuid || "",
      });
    });

    this.clientSecret = setupIntent.client_secret;

    log.info("Created or updated setup intent");

    return setupIntent;
  }

  /**
   * Translates a typed Stripe SDK error into the appropriate
   * PaymentOrchestrationError subclass. Only card_error and
   * validation_error are user-facing declines; everything else
   * is an infrastructure/engineering problem.
   */
  private adaptSdkError(error: StripeError): never {
    const msg = error.message || "We had trouble processing this transaction";

    if (error.type === "card_error" || error.type === "validation_error") {
      throw new PaymentConfirmationError(msg, {
        metadata: {
          error: serializeStripeError(error),
          stripeErrorType: error.type,
          stripeErrorCode: error.code,
          declineCode: error.decline_code,
          gatewayName: StripePaymentGateway.gatewayName,
          operation: "purchase",
        },
        userMessage: msg,
      });
    }

    throw new PaymentProcessingError(
      `Stripe SDK error (${error.type}): ${msg}`,
      {
        metadata: {
          error: serializeStripeError(error),
          stripeErrorType: error.type,
          stripeErrorCode: error.code,
          gatewayName: StripePaymentGateway.gatewayName,
          operation: "purchase",
        },
        userMessage: "We had trouble processing this transaction",
      }
    );
  }

  /**
   * Begin a confirmation flow
   * Confirms a payment intent based on the class' secret.
   */
  public async confirmPayment(
    confirmationStrategy: PAYMENT_MANAGER_CONFIRMATION_STRATEGY,
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    if (!this.instance) {
      throw new PaymentInitializationError(
        "Tried to confirm without a Stripe instance",
        {
          metadata: { gatewayName: "StripePaymentGateway" },
        }
      );
    }

    log.info(`Confirming payment: ${confirmationStrategy}`);

    switch (confirmationStrategy) {
      case PAYMENT_MANAGER_CONFIRMATION_STRATEGY.MANUAL_CARD_ENTRY: {
        return this.confirmCardPayment(basketType, basket, purchaseOpts);
      }
      case PAYMENT_MANAGER_CONFIRMATION_STRATEGY.WALLET: {
        return this.confirmWalletPayment(basketType, basket);
      }
      case PAYMENT_MANAGER_CONFIRMATION_STRATEGY.BUY_NOW_PAY_LATER: {
        // All BNPLs on our Stripe gateways currently only go through affirm
        return this.confirmAffirmPayment(basketType, basket, purchaseOpts);
      }
      // default to card if somehow no strategy could be handled
      default: {
        return this.confirmCardPayment(basketType, basket, purchaseOpts);
      }
    }
  }

  /**
   * Confirms a deferred payment by using the SetupIntents API to
   * create a payment method that can be charged off-session in the future.
   *
   * Uses the Token strategy to capture payment method details, from the card
   * information collected via elements rendered through the elementsInstance
   * https://docs.stripe.com/js/setup_intents/confirm_card_setup#stripe_confirm_card_setup-token
   */
  public async confirmDeferredPayment(
    _: PAYMENT_MANAGER_CONFIRMATION_STRATEGY,
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    if (!this.instance) {
      throw new PaymentInitializationError(
        "Tried to confirm a deferred card payment without a Stripe instance",
        {
          metadata: { gatewayName: "StripePaymentGateway" },
        }
      );
    }

    await this.updatePaymentObject(basket, {
      // Deferred payments always require SetupIntents
      forceUpsert: true,
    });

    if (!this.clientSecret) {
      throw new PaymentConfigurationError(
        "Tried to confirm a deferred card payment without a valid Stripe configuration"
      );
    }

    log.info("Confirming deferred payment");

    const token = await this.createPaymentToken(
      basketType,
      basket,
      purchaseOpts
    );

    if (!token.token) {
      throw new PaymentProcessingError(
        "Could not generate a secure payment token for Stripe",

        {
          userMessage:
            "We had trouble processing your payment information. Please check your card details and try again.",
        }
      );
    }

    log.info("Created payment token");

    await this.validatePurchaseData(
      basketType,
      basket,
      token.token.id,
      purchaseOpts
    );

    const setupIntentResult = await this.instance.confirmCardSetup(
      this.clientSecret,
      {
        payment_method: {
          card: {
            token: token.token.id,
          },
        },
      }
    );

    if (setupIntentResult.error) {
      return this.adaptSdkError(setupIntentResult.error);
    }

    log.info("Confirmed deferred payment");

    return setupIntentResult;
  }

  /**
   * Confirms an affirm payment on Stripe using the user input on the address elements
   */
  private async confirmAffirmPayment(
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    if (!this.instance || !this.elementsInstance) {
      throw new PaymentInitializationError(
        "Tried to confirm an affirm payment without a Stripe instance",
        {
          metadata: {
            gatewayName: "StripePaymentGateway",
            hasInstance: Boolean(this.instance),
            hasElementsInstance: Boolean(this.elementsInstance),
          },
        }
      );
    }

    // Double call the /payment-intent endpoint due to a restriction of `setup_future_usage` being set to `on_session`
    // when a basket has never had a `stripe_wallet_payment_intent_id`
    // @see https://bitbucket.org/showpass/web-app/src/b179b650cae1f682c9494b51340e23080e3781d1/apps/tickets/models/order_management/order_basket.py#lines-3057
    // Affirm payments always requires PaymentIntents
    if (!this.clientSecret) {
      await this.updatePaymentObject(basket, {
        forceUpsert: true,
      });
    }
    await this.updatePaymentObject(basket, {
      forceUpsert: true,
    });

    if (!this.clientSecret) {
      throw new PaymentConfigurationError(
        "Tried to confirm an affirm payment without a valid Stripe configuration"
      );
    }

    // validate purchase data
    await this.validatePurchaseData(basketType, basket);

    const { billingDetails, shipping } = this.createAddressObjects(
      basket,
      purchaseOpts
    );

    const affirmPaymentParams: CleanedConfirmAffirmPaymentData = {
      payment_method: {
        billing_details: billingDetails,
      },
      return_url: `${
        purchaseOpts.redirect_url
          ? securedUrl(
              ensuredUrl(purchaseOpts.redirect_url, getRedirectReturnUrl())
            )
          : getRedirectReturnUrl()
      }&affirm=true`,
    };

    // Conditionally include properties as Stripe does not like
    // passing keys that have nullish values
    if (shipping) {
      affirmPaymentParams.shipping = shipping;
    }

    const affirmPaymentResult = await this.instance.confirmAffirmPayment(
      this.clientSecret,
      affirmPaymentParams
    );

    if (affirmPaymentResult.error) {
      return this.adaptSdkError(affirmPaymentResult.error);
    }

    return affirmPaymentResult;
  }

  /**
   * Confirm a card payment using a client secret.
   *
   * Uses the Token strategy to capture payment method details, from the card
   * information collected via elements rendered through the elementsInstance
   * https://docs.stripe.com/js/setup_intents/confirm_card_setup#stripe_confirm_card_setup-token
   */
  private async confirmCardPayment(
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    if (this.venuePaymentIntentsEnabled) {
      return this.confirmCardPaymentWithPaymentIntents(
        basketType,
        basket,
        purchaseOpts
      );
    }

    return this.confirmCardPaymentWithCharges(basketType, basket, purchaseOpts);
  }

  /**
   * Confirm a card payment using Stripe Payment Intents.
   * https://docs.stripe.com/api/payment_intents
   *
   * Uses the Token strategy to capture payment method details, from the card
   * information collected via elements rendered through the elementsInstance
   * https://docs.stripe.com/js/setup_intents/confirm_card_setup#stripe_confirm_card_setup-token
   */
  private async confirmCardPaymentWithPaymentIntents(
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    if (!this.instance) {
      throw new PaymentInitializationError(
        "Tried to confirm a card payment without a Stripe instance",
        {
          metadata: { gatewayName: "StripePaymentGateway" },
        }
      );
    }

    if (!this.basketPaymentService) {
      throw new PaymentInitializationError(
        "Tried to confirm a card payment without a basket service",
        {
          metadata: { gatewayName: "StripePaymentGateway" },
        }
      );
    }

    const token = await this.createPaymentToken(
      basketType,
      basket,
      purchaseOpts
    );

    if (!token.token) {
      throw new PaymentProcessingError(
        "Could not generate a secure payment token for Stripe",
        {
          userMessage:
            "We had trouble processing your payment information. Please check your card details and try again.",
        }
      );
    }

    log.info("Created payment token");

    // PaymentIntent objects are lifecycle objects, so keep it in sync.
    await this.updatePaymentObject(basket);

    if (!this.clientSecret) {
      throw new PaymentConfigurationError(
        "Tried to confirm a card payment without a valid Stripe configuration"
      );
    }

    // validate purchase data
    await this.validatePurchaseData(
      basketType,
      basket,
      token.token.id,
      purchaseOpts
    );

    const { billingDetails, shipping } = this.createAddressObjects(
      basket,
      purchaseOpts
    );

    const cardPaymentParams: Parameters<
      typeof this.instance.confirmCardPayment
    >[1] = {
      payment_method: {
        card: { token: token.token.id },
        billing_details: billingDetails,
      },
    };

    // Conditionally include properties as Stripe does not like
    // passing keys that have nullish values
    if (shipping) {
      cardPaymentParams.shipping = shipping;
    }

    const paymentIntentResult = await this.instance.confirmCardPayment(
      this.clientSecret,
      cardPaymentParams
    );

    if (paymentIntentResult.error) {
      return this.adaptSdkError(paymentIntentResult.error);
    }

    return paymentIntentResult;
  }

  /**
   * Confirm a card payment using Stripe Charges API.
   * https://docs.stripe.com/api/charges
   *
   * Uses the Token strategy to capture payment method details, from the card
   * information collected via elements rendered through the elementsInstance
   * https://docs.stripe.com/js/setup_intents/confirm_card_setup#stripe_confirm_card_setup-token
   */
  private async confirmCardPaymentWithCharges(
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    if (!this.instance) {
      throw new PaymentInitializationError(
        "Tried to confirm a card payment without a Stripe instance",
        {
          metadata: { gatewayName: "StripePaymentGateway" },
        }
      );
    }

    if (!this.basketPaymentService) {
      throw new PaymentInitializationError(
        "Tried to confirm a card payment without a basket service",
        {
          metadata: { gatewayName: "StripePaymentGateway" },
        }
      );
    }

    // Check if basket has a payment method attached
    // In the future, stop looking at baskets for information about how to approach purchase.
    // baskets are living objects and has a lifecycle, where purchasing flows are idempotent and linear.
    if (basket.paymentMethod) {
      // Call /purchase without a token

      await this.basketPaymentService.updateIsStripeWalletPayment(false);
      await this.basketPaymentService.save();

      return this.purchase(basketType, basket, undefined, purchaseOpts).catch(
        (err) => {
          return this.handleConfirmError(err);
        }
      );
    }

    // Create a payment token and use that in the /purchase call

    const token = await this.createPaymentToken(
      basketType,
      basket,
      purchaseOpts
    );

    if (!token.token) {
      throw new PaymentProcessingError(
        "Could not generate a secure payment token for Stripe",

        {
          userMessage:
            "We had trouble processing your payment information. Please check your card details and try again.",
        }
      );
    }

    log.info("Created payment token");

    await this.basketPaymentService.updateIsStripeWalletPayment(false);
    await this.basketPaymentService.save();

    return this.purchase(
      basketType,
      basket,
      token.token.id,
      purchaseOpts
    ).catch((err) => {
      return this.handleConfirmError(err);
    });
  }

  /**
   * Confirms a wallet payment rendered through the elementsInstance
   *
   * Internally, we wrap every function call made before the Stripe instance `confirmPayment` call in a
   * try-catch in order to propagate a StripeWalletPaymentError instead of the original error. This is meant to
   * be a signal to an Express Checkout Element callee that an error has occurred and that we must call `event.paymentFailed`.
   * https://docs.stripe.com/js/elements_object/express_checkout_element_confirm_event
   */
  private async confirmWalletPayment(
    basketType: DataProvider,
    basket: PaymentServiceBasket
  ) {
    if (!this.instance) {
      throw new StripeWalletPaymentError(
        "Tried to confirm a wallet payment without a Stripe instance"
      );
    }

    if (!this.elementsInstance) {
      throw new StripeWalletPaymentError(
        "Tried to confirm a wallet payment without a Stripe elements instance"
      );
    }

    try {
      await this.updatePaymentObject(basket, {
        // Wallet payments always require PaymentIntents
        forceUpsert: true,
      });
    } catch (err) {
      // Intercept updatePaymentObject error and turn it into a StripeWalletPaymentError instance
      // with the original error message as the message
      throw new StripeWalletPaymentError(getExceptionToastDescription(err));
    }

    if (!this.clientSecret) {
      throw new StripeWalletPaymentError(
        "Tried to confirm a wallet payment without a valid Stripe configuration"
      );
    }

    // validate purchase data
    try {
      await this.validatePurchaseData(basketType, basket);
    } catch (err) {
      // Intercept validatePurchaseData error and turn it into a StripeWalletPaymentError instance
      // with the original error message as the message
      throw new StripeWalletPaymentError(getExceptionToastDescription(err));
    }

    const paymentIntentResult = await this.instance.confirmPayment({
      elements: this.elementsInstance,
      clientSecret: this.clientSecret,
      confirmParams: {
        return_url: `${getRedirectReturnUrl()}&wallet=true`,
      },
      redirect: "if_required",
    });

    if (paymentIntentResult.error) {
      return this.adaptSdkError(paymentIntentResult.error);
    }

    return paymentIntentResult;
  }

  /**
   * Formats an address object to match Stripe's expected address format
   * @param address The address object to format
   * @returns Address object formatted for Stripe API
   */
  private static formatAddressForStripe(address: {
    street_address?: string;
    unit_number?: string;
    city?: string;
    province?: string;
    postal?: string;
    country?: string;
  }): {
    line1: string;
    line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  } {
    const stripeAddress: {
      line1: string;
      line2?: string;
      city?: string;
      state?: string;
      postal_code?: string;
      country?: string;
    } = {
      line1: "",
    };

    // Only add properties that exist to avoid Stripe validation errors on empty fields
    // Address line details
    if (address?.street_address) {
      stripeAddress.line1 = address.street_address;
    }
    if (address?.unit_number) {
      stripeAddress.line2 = address.unit_number;
    }

    // Municipality & Provincial
    if (address?.city) {
      stripeAddress.city = address.city;
    }
    if (address?.province) {
      stripeAddress.state = address.province;
    }
    if (address?.postal) {
      stripeAddress.postal_code = address.postal;
    }
    // Country, as a ISO-3166-1 alpha-2 standard format
    // This is used for both single-use Token objects and PaymentMethod objects
    if (address?.country) {
      stripeAddress.country = formatCountryToISOCode(address.country);
    }

    return stripeAddress;
  }

  /**
   * Creates a payment method object with common billing details
   * to be used in various payment confirmation methods
   */
  private createAddressObjects(
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    // Base billing details structure
    const billingDetails = {
      email: basket.email || "",
      name: basket.nameOnTicket || "",
      phone: basket.phoneNumber || "",
      address: {},
    };

    // Address handling
    const billingAddress =
      purchaseOpts.billing_address || basket.billingAddress;
    const shippingAddress =
      purchaseOpts.shipping_address || basket.shippingAddress;

    if (billingAddress) {
      billingDetails.address =
        StripePaymentGateway.formatAddressForStripe(billingAddress);
    }

    // Create shipping object if shipping address exists
    const shipping = shippingAddress
      ? {
          name: basket.nameOnTicket || "",
          address: StripePaymentGateway.formatAddressForStripe(shippingAddress),
        }
      : undefined;

    return { billingDetails, shipping };
  }

  /**
   * Creates a token to use for a purchase using the user input on the CardElement
   * and any other necessary elements
   */
  private async createPaymentToken(
    _: DataProvider,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    if (!this.instance || !this.elementsInstance) {
      throw new PaymentInitializationError(
        "Tried to create a payment token without a Stripe instance",
        {
          metadata: {
            gatewayName: "StripePaymentGateway",
            hasInstance: !!this.instance,
            hasElementsInstance: !!this.elementsInstance,
          },
        }
      );
    }

    const cardNumberEl = this.elementsInstance.getElement("cardNumber");
    if (!cardNumberEl) {
      throw new PaymentInitializationError(
        "Could not find a mounted cardNumber element to generate a token for",
        {
          metadata: { gatewayName: "StripePaymentGateway" },
        }
      );
    }
    const name = purchaseOpts.name_on_card;
    const billingAddress =
      purchaseOpts.billing_address || basket.billingAddress;

    const tokenCardData: CreateTokenCardData = {};

    if (name) {
      tokenCardData.name = name;
    }

    // Format the address data using our utility function if billing address exists
    if (billingAddress) {
      const stripeAddress =
        StripePaymentGateway.formatAddressForStripe(billingAddress);

      if (stripeAddress.country)
        tokenCardData.address_country = stripeAddress.country;
      if (stripeAddress.state)
        tokenCardData.address_state = stripeAddress.state;
      if (stripeAddress.city) tokenCardData.address_city = stripeAddress.city;
      if (stripeAddress.postal_code)
        tokenCardData.address_zip = stripeAddress.postal_code;
      if (stripeAddress.line1)
        tokenCardData.address_line1 = stripeAddress.line1;
      if (stripeAddress.line2)
        tokenCardData.address_line2 = stripeAddress.line2;
    }

    const tokenResult = await this.instance.createToken(
      cardNumberEl,
      tokenCardData
    );

    if (tokenResult.error) {
      throw new PaymentProcessingError(
        `Stripe token creation failed: ${tokenResult.error.message}`,
        {
          metadata: {
            error: serializeStripeError(tokenResult.error),
          },
          userMessage:
            tokenResult.error.message ??
            "We had trouble processing this transaction",
        }
      );
    }

    return tokenResult;
  }

  /**
   * Performs a `/purchase` call to the basket, essentially purchasing the basket via Charges API.
   * This should only be used for venues that have PaymentIntents *disabled*. When this flow is followed, it means that the purchase
   * flow will not follow an ASYNCHRONOUS_CONFIRMATION and ASYNCHRONOUS_CAPTURE flow.
   * In other words, we will not be handling long-running client-side flows like 3DS,
   * and we won't have to wait for a payment capture result.
   *
   * For more information about ASYNCHRONOUS_CONFIRMATION and ASYNCHRONOUS_CAPTURE,
   * @see {@link PaymentHandler}
   */
  // eslint-disable-next-line class-methods-use-this
  private async purchase(
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    token?: string,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    log.info("Purchase call");

    if (!basket.basketId) {
      throw new PaymentProcessingError(
        "Cannot perform a purchase call with an invalid basket id",
        {
          userMessage:
            "There was an issue with your cart. Please refresh and try again.",
        }
      );
    }

    const repository:
      | VenueBasedTicketBasketRepository
      | UserBasedTicketBasketRepository
      | undefined = BasketRepositoryFactory.getRepository(
      basketType,
      basket.paymentVenue
    );

    if (!repository) {
      throw new PaymentConfigurationError(
        "Invalid repository selected to perform purchase call"
      );
    }

    const purchaseData = buildBasePurchaseData(
      basketType,
      basket,
      purchaseOpts
    );

    // If there is a token passed in, set this as the credit_card data
    if (token) {
      purchaseData.credit_card = {
        charge_type: "ct_token",
        token_id: token,
      };
    }

    const { billingAddress, shippingAddress } = resolveAddresses(
      basket,
      purchaseOpts
    );

    if (billingAddress) {
      purchaseData.billing_address = billingAddress;
    }

    if (shippingAddress) {
      purchaseData.shipping_address = shippingAddress;
    }

    return repository.purchase(basket.basketId, purchaseData, {
      showToastOnError: false, // Should be handled by PaymentManager
    });
  }

  /**
   * Validate the purchase data based on the basket data.
   * This should only be used for venues that have PaymentIntents *enabled*. When this flow is followed, it means that the purchase
   * flow is an ASYNCHRONOUS_CONFIRMATION and ASYNCHRONOUS_CAPTURE flow.
   * In other words, we allow handling of long-running client-side flows like 3DS and BNPL forms,
   * and we will get the payment capture result asynchronously via Webhooks.
   *
   * Can be expanded to perform other validations
   */
  // eslint-disable-next-line class-methods-use-this
  private async validatePurchaseData(
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    token?: string,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    log.info("Validating purchase data");

    if (!basket.basketId) {
      throw new PaymentProcessingError(
        "Cannot perform a purchase call with an invalid basket id",
        {
          userMessage:
            "There was an issue with your cart. Please refresh and try again.",
        }
      );
    }

    const repository:
      | VenueBasedTicketBasketRepository
      | UserBasedTicketBasketRepository
      | undefined = BasketRepositoryFactory.getRepository(
      basketType,
      basket.paymentVenue
    );

    if (!repository) {
      throw new PaymentConfigurationError(
        "Invalid repository selected to perform purchase call"
      );
    }

    const noPurchaseData = buildBasePurchaseData(
      basketType,
      basket,
      purchaseOpts
    );
    noPurchaseData.no_purchase = true;

    // If there is a token passed in, set this as the credit_card data
    if (token) {
      noPurchaseData.credit_card = {
        charge_type: "ct_token",
        token_id: token,
      };
    }

    const { billingAddress, shippingAddress } = resolveAddresses(basket);

    if (billingAddress) {
      noPurchaseData.billing_address = billingAddress;
    }

    if (shippingAddress) {
      noPurchaseData.shipping_address = shippingAddress;
    }

    return repository.purchase(basket.basketId, noPurchaseData, {
      showToastOnError: false, // Should be handled by PaymentManager
    });
  }

  async postProcessCheckout() {
    log.info("Checking for invoice");

    /* eslint-disable no-await-in-loop -- https://eslint.org/docs/latest/rules/no-await-in-loop#when-not-to-use-it */
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    if (!this.basketPaymentService || !this.invoicePaymentService) {
      throw new PaymentInitializationError(
        "Tried to post process a checkout without required payment services",
        {
          metadata: {
            hasBasketService: Boolean(this.basketPaymentService),
            hasInvoiceService: Boolean(this.invoicePaymentService),
          },
        }
      );
    }

    // First, poll for the basket until we get a valid invoice_id
    // Set above 5 for async payment flows to account for webhook delays
    const MAX_BASKET_ATTEMPTS = 10;
    let finalBasket;
    let invoiceId = null;

    for (let attempts = 1; attempts <= MAX_BASKET_ATTEMPTS; attempts++) {
      finalBasket = await this.basketPaymentService.refresh({
        no_lock: true,
      });

      if (finalBasket?.id == null) {
        throw new PaymentProcessingError(
          "Could not obtain a basket id for post purchase",
          {
            metadata: {
              attempts,
            },
            userMessage:
              "We're having trouble completing your order. Please refresh the page and try again.",
          }
        );
      }

      // If we have an invoice ID, break out of the polling loop
      if (finalBasket.invoice_id != null) {
        invoiceId = finalBasket.invoice_id;
        break;
      }

      // Wait before trying again if we haven't found an invoice_id yet
      if (attempts < MAX_BASKET_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }

    // If we couldn't get an invoice_id after all attempts, throw an error
    if (invoiceId == null) {
      if (
        finalBasket?.payment_intent_status ===
        STRIPE_PAYMENT_INTENT_STATUS_MAP.SUCCEEDED.id
      ) {
        throw new PaymentProcessingError(
          "Payment succeeded, but no invoice ID was found",
          {
            metadata: {
              payment_intent_status: finalBasket?.payment_intent_status,
            },
            userMessage:
              "Your payment was processed but we're having trouble generating your receipt. Please contact support.",
          }
        );
      }
      const errorMessage =
        finalBasket?.payment_intent_outcome_message ||
        "Could not obtain an invoice ID for this purchase";
      throw new PaymentProcessingError(
        `Could not obtain invoice ID: ${errorMessage}`,
        {
          metadata: { attempts: MAX_BASKET_ATTEMPTS },
          userMessage:
            "We're having trouble completing your order. Please try again or contact support.",
        }
      );
    }

    if (finalBasket == null) {
      throw new PaymentProcessingError(
        "Could not obtain a basket for post purchase",
        {
          metadata: { finalBasket },
          userMessage:
            "We're having trouble completing your order. Please try again.",
        }
      );
    }

    return finalBasket;
  }
}

export default StripePaymentGateway;

type CleanedConfirmAffirmPaymentDataPaymentMethod = Exclude<
  ConfirmAffirmPaymentData["payment_method"],
  undefined | string
>;
type CleanedConfirmAffirmPaymentDataPaymentMethodBillingDetails = Exclude<
  CleanedConfirmAffirmPaymentDataPaymentMethod["billing_details"],
  undefined | string
>;
type CleanedConfirmAffirmPaymentDataPaymentMethodBillingDetailsAddress =
  Exclude<
    CleanedConfirmAffirmPaymentDataPaymentMethodBillingDetails["address"],
    undefined
  >;
type CleanedConfirmAffirmPaymentData = ConfirmAffirmPaymentData & {
  payment_method: Exclude<
    ConfirmAffirmPaymentData["payment_method"] & {
      billing_details: CleanedConfirmAffirmPaymentDataPaymentMethodBillingDetails & {
        address: CleanedConfirmAffirmPaymentDataPaymentMethodBillingDetailsAddress;
      };
    },
    undefined | string
  >;
};
